import { useState, useRef, useEffect } from "react";

/**
 * useDraggable - React hook to make any element draggable by mouse or touch.
 * @param {Object} options
 *   initialPos: { x, y } - initial position
 *   bounds: { minX, minY, maxX, maxY } - optional bounds for movement
 *   enabled: boolean - whether dragging is enabled
 * @returns {
 *   dragProps: { onMouseDown, onTouchStart, style },
 *   position: { x, y },
 *   setPosition: function
 * }
 */
export function useDraggable({ initialPos = { x: 0, y: 0 }, bounds, enabled = true } = {}) {
    const [position, setPosition] = useState(initialPos);
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    // Start drag
    const handleDragStart = (e) => {
        if (!enabled) return;
        setDragging(true);
        const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
        dragOffset.current = {
            x: clientX - position.x,
            y: clientY - position.y,
        };
        document.body.style.userSelect = "none";
    };

    // Dragging
    const handleDrag = (e) => {
        if (!dragging) return;
        const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
        let x = clientX - dragOffset.current.x;
        let y = clientY - dragOffset.current.y;
        if (bounds) {
            x = Math.max(bounds.minX, Math.min(bounds.maxX, x));
            y = Math.max(bounds.minY, Math.min(bounds.maxY, y));
        }
        setPosition({ x, y });
    };

    // End drag
    const handleDragEnd = () => {
        setDragging(false);
        document.body.style.userSelect = "";
    };

    useEffect(() => {
        if (!dragging) return;
        window.addEventListener("mousemove", handleDrag);
        window.addEventListener("mouseup", handleDragEnd);
        window.addEventListener("touchmove", handleDrag);
        window.addEventListener("touchend", handleDragEnd);
        return () => {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleDragEnd);
            window.removeEventListener("touchmove", handleDrag);
            window.removeEventListener("touchend", handleDragEnd);
        };
    }, [dragging]);

    // Props to spread on draggable element
    const dragProps = {
        onMouseDown: enabled ? handleDragStart : undefined,
        onTouchStart: enabled ? handleDragStart : undefined,
        style: {
            position: "fixed",
            left: position.x,
            top: position.y,
            cursor: dragging ? "grabbing" : "grab",
            zIndex: 99999999,
        },
    };

    return { dragProps, position, setPosition };
}
